import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { loadLayout } from '../../data'
import type { CloudLayout, CloudLayoutSection } from '../../models'
import { addDetailedItems, buildAppendItemsPrompt } from '../parityData'
import type { CoreCtx } from './CoreScreens'
import { copy, errorMessage, Field, FilledButton, Notice, OutlinedButton, Screen, TopBar } from '../components/ParityUi'

const categoryName = (section: CloudLayoutSection, language: 'en' | 'sv') => section.categoryNames[language] || section.categoryNames.en || section.categoryNames.sv || section.categoryId

const safeCopy = async (text: string) => {
  if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text)
  else {
    const textarea = document.createElement('textarea')
    textarea.value = text
    document.body.append(textarea)
    textarea.select()
    document.execCommand('copy')
    textarea.remove()
  }
}

export default function AddManyItemsScreen({ ctx }: { ctx: CoreCtx }) {
  const { listId } = useParams()
  const navigate = useNavigate()
  const list = ctx.lists.find((row) => row.id === listId)
  const [layout, setLayout] = useState<CloudLayout | null>(null)
  const [json, setJson] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (list?.layoutId) void loadLayout(list.layoutId).then(setLayout).catch((cause) => setMessage(errorMessage(cause)))
    else setLayout(null)
  }, [list?.layoutId])

  if (!list) return <Screen><TopBar title={copy(ctx.language, 'Add many items', 'Lägg till många varor')} back={() => navigate('/pwa/')} /><div className="screen-scroll"><Notice>{copy(ctx.language, 'List unavailable.', 'Listan är inte tillgänglig.')}</Notice></div></Screen>
  const allowed = new Set(layout?.sections.map((section) => section.categoryId) ?? [])
  const importNow = () => {
    try {
      const root = JSON.parse(json) as { items?: Array<Record<string, unknown>> }
      if (!Array.isArray(root.items)) throw new Error(copy(ctx.language, 'JSON must contain an items array.', 'JSON måste innehålla en items-array.'))
      addDetailedItems(list, root.items, allowed)
      setJson('')
      setMessage(copy(ctx.language, 'Items imported', 'Varor importerade'))
    } catch (cause) {
      setMessage(errorMessage(cause))
    }
  }

  return <Screen>
    <TopBar title={copy(ctx.language, 'Add many items with AI', 'Lägg till många varor med AI')} back={() => navigate(`/pwa/list/${list.id}/add`)} />
    <div className="screen-scroll form-stack">
      {message && <Notice>{message}</Notice>}
      <div className="m3-card ai-card">
        <div className="card-title"><Sparkles /><strong>{copy(ctx.language, 'AI import', 'AI-import')}</strong></div>
        <p className="muted">{copy(ctx.language, 'Copy the Shelfway prompt to your AI assistant, add the groceries you want, then paste the returned JSON here.', 'Kopiera Shelfway-prompten till din AI-assistent, lägg till varorna du vill ha och klistra sedan in JSON-svaret här.')}</p>
        {layout && <p className="muted route-preview">{layout.sections.slice().sort((a, b) => a.displayOrder - b.displayOrder).map((section) => categoryName(section, ctx.language)).join(' → ')}</p>}
        <OutlinedButton className="full" onClick={() => void safeCopy(buildAppendItemsPrompt(ctx.language, list, layout))}>{copy(ctx.language, 'Copy AI prompt', 'Kopiera AI-prompt')}</OutlinedButton>
      </div>
      <Field label="Shelfway JSON"><textarea rows={12} value={json} onChange={(event) => setJson(event.target.value)} /></Field>
      <FilledButton className="full" disabled={!json.trim()} onClick={importNow}>{copy(ctx.language, 'Import items', 'Importera varor')}</FilledButton>
    </div>
  </Screen>
}
